import { Head, useForm } from "@inertiajs/react"
import { Button, Flex, Text, TextField } from "@radix-ui/themes"
import { LoaderCircle } from "lucide-react"
import type { FormEventHandler } from "react"

import InputError from "@/components/input-error"
import LinkButton from "@/components/link-button"
import CenteredLayout from "@/layouts/centered-layout"
import { projectSegmentsPath } from "@/routes"

interface SegmentForm {
  name: string
  event_name: string
  event_count: number
}

interface NewSegmentProps {
  project: {
    id: number
    name: string
    slug: string
  }
}

export default function NewSegment({ project }: NewSegmentProps) {
  const { data, setData, post, processing, errors } = useForm<Required<SegmentForm>>({
    name: "",
    event_name: "",
    event_count: 1,
  })

  const submit: FormEventHandler = (e) => {
    e.preventDefault()
    post(projectSegmentsPath(project.slug))
  }

  return (
    <CenteredLayout
      title="Create Segment"
      description="Create a segment to group customers based on events they've performed"
    >
      <Head title="New Segment" />
      <form className="flex flex-col gap-6" onSubmit={submit}>
        <div className="grid gap-6">
          <div className="grid gap-2">
            <Flex justify="between">
              <Text as="label" htmlFor="name" size="2" weight="medium">
                Segment Name
              </Text>

              <InputError message={errors.name} />
            </Flex>

            <TextField.Root
              id="name"
              type="text"
              autoFocus
              tabIndex={1}
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              placeholder="Active users, Paying customers, etc."
            />
          </div>

          <div className="grid gap-2">
            <Flex justify="between">
              <Text as="label" htmlFor="event_name" size="2" weight="medium">
                Event Name
              </Text>

              <InputError message={errors.event_name} />
            </Flex>

            <TextField.Root
              id="event_name"
              type="text"
              tabIndex={2}
              value={data.event_name}
              onChange={(e) => setData("event_name", e.target.value)}
              placeholder="page_viewed, button_clicked, etc."
            />
          </div>

          <div className="grid gap-2">
            <Flex justify="between">
              <Text as="label" htmlFor="event_count" size="2" weight="medium">
                Minimum Event Count
              </Text>

              <InputError message={errors.event_count} />
            </Flex>

            <TextField.Root
              id="event_count"
              type="number"
              tabIndex={3}
              value={data.event_count.toString()}
              onChange={(e) => setData("event_count", parseInt(e.target.value, 10))}
              placeholder="1"
              min="1"
            />
          </div>

          <Flex gap="3" mt="4">
            <Button type="submit" className="w-full" tabIndex={4} disabled={processing}>
              {processing && <LoaderCircle className="h-4 w-4 animate-spin" />}
              Create Segment
            </Button>

            <LinkButton
              href={projectSegmentsPath(project.slug)}
              variant="soft"
              className="w-full"
              tabIndex={5}
            >
              Cancel
            </LinkButton>
          </Flex>
        </div>
      </form>
    </CenteredLayout>
  )
}
